import { cn } from "../../../utils/cn.js";
import { OverlayStackContext, OverlaySurfaceContext, getOverlayLayerZIndex, useOverlaySurfaceState } from "../overlay-layering.js";
import { cva } from "class-variance-authority";
import { XIcon } from "@phosphor-icons/react";
import * as React from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { Dialog as Dialog$1 } from "@base-ui/react/dialog";
//#region src/components/Dialog/Dialog.tsx
/**
* Count of ancestor `DialogContent` layers wrapping the current subtree.
* Used to elevate z-index for nested dialogs so each layer's backdrop sits
* above the parent popup (blocking interaction) while staying paired with
* its own popup.
*/
const DialogNestingContext = React.createContext(0);
/**
* Groups all parts of the dialog. Delegates directly to the Base UI Dialog.Root
* primitive without rendering its own element.
*
* @param props - Dialog state, modal behavior, and open/close callbacks
*/
function Dialog(props) {
	return /* @__PURE__ */ jsx(Dialog$1.Root, { ...props });
}
/**
* Renders the element that opens the dialog on interaction.
*
* @param props - Trigger element props forwarded to Base UI Dialog.Trigger
* @returns The rendered trigger element
*/
function DialogTrigger(props) {
	return /* @__PURE__ */ jsx(Dialog$1.Trigger, { ...props });
}
/**
* Portals dialog content to the end of the document body, avoiding stacking
* context issues.
*
* @param props - Portal configuration forwarded to Base UI Dialog.Portal
*/
function DialogPortal(props) {
	return /* @__PURE__ */ jsx(Dialog$1.Portal, { ...props });
}
/**
* Semi-transparent backdrop rendered behind the dialog popup.
*
* Fades in/out using `data-[starting-style]` and `data-[ending-style]`
* transition attributes provided by Base UI.
*
* @param props - Overlay props including optional className overrides
* @returns The rendered overlay element
*/
function DialogOverlay({ className, ...props }) {
	return /* @__PURE__ */ jsx(Dialog$1.Backdrop, {
		className: cn("fixed inset-0 z-[100010] bg-black/50", "transition-all duration-150", "data-[starting-style]:opacity-0 data-[ending-style]:opacity-0", className),
		...props
	});
}
const dialogContentVariants = cva([
	"max-w-[95vw]",
	"fixed left-1/2 -translate-x-1/2 -translate-y-1/2",
	"top-[calc(50%+1.25rem*var(--nested-dialogs))]",
	"scale-[calc(1-0.1*var(--nested-dialogs))]",
	"flex font-body",
	"border border-(length:--border-width-default) border-border-primary bg-elevation-surface-overlay shadow-elevation-overlay",
	"outline-none",
	"transition-all duration-150",
	"data-[starting-style]:scale-95 data-[starting-style]:opacity-0",
	"data-[ending-style]:scale-95 data-[ending-style]:opacity-0",
	"after:pointer-events-none after:absolute after:inset-0 after:z-10 after:rounded-[inherit] after:bg-black/0 after:transition-colors after:duration-150",
	"data-[nested-dialog-open]:after:bg-black/20"
], {
	defaultVariants: { variant: "default" },
	variants: { variant: {
		default: "min-h-[180px] w-[480px] flex-col items-start justify-between rounded-sm p-md",
		education: "h-[514px] w-[860px] flex-row items-stretch overflow-clip rounded-sm-m"
	} }
});
/**
* Curri-styled dialog popup container built on Base UI primitives.
*
* Renders inside a portal with an optional backdrop overlay. Supports two
* layout variants: `"default"` for standard confirmation dialogs (480 px,
* column layout) and `"education"` for wide split-panel dialogs with a
* content area and image panel (860 px, row layout). Uses Base UI's
* `data-[starting-style]` / `data-[ending-style]` attributes for enter/exit
* CSS transitions.
*
* Dialogs participate in the shared overlay stack via `OverlayStackContext`,
* so dialogs, alert dialogs, and drawers can all layer above one another
* consistently. Parent-surface tinting is coordinated through
* `OverlaySurfaceContext`, which lets nested overlays darken the surface
* beneath them without forcing drawers to adopt dialog-style scale/minimize
* behavior. Dialog-specific visual offset/scale still comes from
* `DialogNestingContext`, so only dialogs nested inside other dialogs get the
* centered stacking transform.
*
* @param props - Popup configuration, variant, overlay toggle, and class overrides
* @returns The rendered dialog content panel
*/
function DialogContent({ className, variant, showOverlay, overlayClassName, children, style, ...props }) {
	const overlayDepth = React.useContext(OverlayStackContext);
	const dialogAncestorDepth = React.useContext(DialogNestingContext);
	const [popupElement, setPopupElement] = React.useState(null);
	const { hasNestedOverlay, registerNestedOverlay } = useOverlaySurfaceState(popupElement !== null);
	const isNestedOverlay = overlayDepth > 0;
	const { overlay: zOverlay, popup: zPopup } = getOverlayLayerZIndex(overlayDepth);
	return /* @__PURE__ */ jsxs(DialogPortal, { children: [showOverlay !== false && /* @__PURE__ */ jsx(DialogOverlay, {
		forceRender: isNestedOverlay || void 0,
		className: cn(isNestedOverlay && showOverlay === void 0 && "bg-transparent", overlayClassName),
		style: { zIndex: zOverlay }
	}), /* @__PURE__ */ jsx(Dialog$1.Popup, {
		ref: setPopupElement,
		className: cn(dialogContentVariants({ variant }), hasNestedOverlay && "after:bg-black/20", className),
		style: {
			...style,
			zIndex: zPopup
		},
		...props,
		children: /* @__PURE__ */ jsx(OverlayStackContext.Provider, {
			value: overlayDepth + 1,
			children: /* @__PURE__ */ jsx(OverlaySurfaceContext.Provider, {
				value: registerNestedOverlay,
				children: /* @__PURE__ */ jsx(DialogNestingContext.Provider, {
					value: dialogAncestorDepth + 1,
					children
				})
			})
		})
	})] });
}
/**
* Semantic wrapper for the dialog title/description area.
* Stacks children vertically with an 8 px gap.
*
* @param props - Standard div props
* @returns The rendered header container
*/
function DialogHeader({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		className: cn("flex w-full flex-col gap-xs", className),
		...props
	});
}
/**
* Semantic wrapper for dialog action buttons.
* Lays out children in a horizontal row with space-between alignment matching
* the Figma button dock.
*
* @param props - Standard div props
* @returns The rendered footer container
*/
function DialogFooter({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		className: cn("flex w-full items-center justify-between", className),
		...props
	});
}
/**
* Renders the dialog heading using the Base UI Dialog.Title primitive for
* proper `aria-labelledby` wiring.
*
* Styled as the Figma "heading/medium" treatment: 18 px semibold with tight
* letter-spacing.
*
* @param props - Title props forwarded to Base UI Dialog.Title
* @returns The rendered title element
*/
function DialogTitle({ className, ...props }) {
	return /* @__PURE__ */ jsx(Dialog$1.Title, {
		className: cn("m-0 font-body text-heading-medium leading-heading-medium font-heading-semi-bold tracking-xs text-text-primary", className),
		...props
	});
}
/**
* Renders the dialog body text using the Base UI Dialog.Description primitive
* for proper `aria-describedby` wiring.
*
* Styled with the Figma "body/medium" treatment: 12 px medium weight,
* secondary text color.
*
* @param props - Description props forwarded to Base UI Dialog.Description
* @returns The rendered description element
*/
function DialogDescription({ className, ...props }) {
	return /* @__PURE__ */ jsx(Dialog$1.Description, {
		className: cn("m-0 font-body text-body-medium leading-body-medium font-heading-medium tracking-sm text-text-secondary", className),
		...props
	});
}
/**
* Renders a close trigger that dismisses the dialog.
* Delegates to Base UI Dialog.Close.
*
* @param props - Close trigger props forwarded to Base UI Dialog.Close
* @returns The rendered close trigger
*/
function DialogClose(props) {
	return /* @__PURE__ */ jsx(Dialog$1.Close, { ...props });
}
/**
* Pre-styled close icon button positioned in the dialog heading dock.
*
* Renders a Phosphor X icon inside a Base UI Dialog.Close primitive with
* hover and focus-visible treatments.
*
* @param props - Close button configuration and optional icon size
* @returns The rendered icon close button
*/
function DialogCloseButton({ className, iconSize = 12, ...props }) {
	return /* @__PURE__ */ jsx(Dialog$1.Close, {
		className: cn("inline-flex shrink-0 cursor-pointer items-center justify-center rounded-xs border-0 bg-background-neutral-secondary p-button-height text-text-secondary outline-none", "hover:bg-background-neutral-secondary-hover hover:text-text-primary", "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-text-selected", className),
		...props,
		children: /* @__PURE__ */ jsx(XIcon, { style: {
			width: iconSize,
			height: iconSize
		} })
	});
}
/**
* Horizontal layout container for the dialog title and close button.
* Uses space-between to push the close icon to the trailing edge,
* matching the Figma heading dock pattern.
*
* @param props - Standard div props
* @returns The rendered heading dock row
*/
function DialogHeadingDock({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		className: cn("flex w-full items-center justify-between", className),
		...props
	});
}
/**
* Content panel for the education dialog variant.
*
* Renders the left half of a split-panel dialog with xl padding, a right-side
* divider border, and full-height sizing. Compose inner layout freely — pair
* with `DialogHeader`, `DialogTitle`, `DialogDescription`, and action buttons.
*
* @param props - Standard div props
* @returns The rendered body panel
*/
function DialogBody({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		className: cn("flex h-full flex-1 items-start bg-elevation-surface-overlay p-xl", "border-r border-r-(length:--border-width-default) border-r-border-primary", className),
		...props
	});
}
/**
* Media panel for the education dialog variant.
*
* Renders the right half of a split-panel dialog with a subtle grey
* background and overflow clipping. Position child elements (images, close
* buttons) using standard CSS — the panel is `position: relative` for
* absolute-positioned children like `DialogCloseButton`.
*
* @param props - Standard div props
* @returns The rendered image panel
*/
function DialogImage({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		className: cn("relative flex h-full flex-1 overflow-clip bg-background-accent-grey-subtle", className),
		...props
	});
}
//#endregion
export { Dialog, DialogBody, DialogClose, DialogCloseButton, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogHeadingDock, DialogImage, DialogNestingContext, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger };

//# sourceMappingURL=Dialog.js.map